/* eslint-disable */
// /src/pages/SalesRankingCorpPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import BrandHeader from "../components/BrandHeader";
import PageTopBar from "../components/PageTopBar";
import { motion, MotionConfig, useReducedMotion } from "framer-motion";

import corp1 from "../assets/sales/corp1.png";
import corp2 from "../assets/sales/corp2.png";
import corp3 from "../assets/sales/corp3.png";
import corp4 from "../assets/sales/corp4.png";

import { fetchCorporateTopByFare } from "../services/settlementsRankingService";

/* ================= Utils ================= */
/** "(지역) 이름" → { region, name } */
const parseCorp = (corp) => {
  if (!corp) return { region: "", name: "" };
  const m = corp.match(/^\s*\(([^)]+)\)\s*(.+)$/); // "(지역) 이름"
  if (m) return { region: m[1], name: m[2] };
  // 괄호 없으면 이름만
  return { region: "", name: corp.replace(/\s+/g, " ").trim() };
};

const formatKRW = (v) =>
  v == null || isNaN(v) ? "0원" : `${Number(v).toLocaleString("ko-KR")}원`;

/** settlementMonthKey → "10월" */
function formatSettlementMonthLabel(key) {
  if (!key) return "";
  const raw = String(key).trim();
  let month;

  // 예: "2025-10"
  if (/^\d{4}-\d{2}$/.test(raw)) {
    const parts = raw.split("-");
    month = parts[1];
  }
  // 예: "202510"
  else if (/^\d{6}$/.test(raw)) {
    month = raw.slice(4);
  } else {
    return "";
  }

  const mNum = Number(month);
  if (!mNum || mNum < 1 || mNum > 12) return "";
  return `${mNum}월`;
}

/* ================= Layout ================= */
const Wrap = styled.div`
  min-height: 100vh;
  background: #eaf3ff;
  display: flex;
  flex-direction: column;
`;
const Sheet = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
`;
const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-rows: auto auto 1fr;
  row-gap: 14px;
  padding: 16px;
`;

/* ================= 3D Tilt Bar (Blue theme) ================= */
const PALETTE = {
  royal: {
    front: "linear-gradient(180deg,#CFE3FF 0%,#9FC2FF 40%,#76A6FF 100%)",
    side: "linear-gradient(180deg,#9BB7F0 0%,#7CA1E6 55%,#6C94DE 100%)",
  },
  steel: {
    front: "linear-gradient(180deg,#EAF0FF 0%,#CBD8FF 40%,#AFC4FF 100%)",
    side: "linear-gradient(180deg,#B9C7EC 0%,#9DAFDE 55%,#8BA0D4 100%)",
  },
  coral: {
    front: "linear-gradient(180deg,#FFE1E6 0%,#FFC2D0 40%,#FF9CB4 100%)",
    side: "linear-gradient(180deg,#E6A7B6 0%,#D68DA0 55%,#C5758B 100%)",
  },
};

/* 로고 이미지 아바타 (원형) */
const CorpAvatarImg = styled.img`
  --size: ${(p) => p.$size || 180}px;
  width: var(--size);
  height: var(--size);
  border-radius: 999px;
  object-fit: cover;
  background: transparent;
  box-shadow: none;

  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: ${(p) => `calc(var(--size) * ${p.$topMul ?? -0.88})`};
  z-index: 3;
`;

const TiltWrap = styled(motion.div)`
  position: relative;
  width: 100%;
  height: ${(p) => p.$h || 240}px;
  transform-origin: bottom center;
  transform-style: preserve-3d;
  transform: perspective(900px) rotateY(${(p) => p.$tilt || 15}deg);
`;
const FrontFace = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 16px 16px 6px 6px;
  clip-path: polygon(8% 0%, 92% 0%, 100% 12%, 100% 100%, 0% 100%, 0% 12%);
  background: ${(p) => p.$bg};
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.22),
    inset 0 0 14px rgba(255, 255, 255, 0.35);
`;
const RightFace = styled.div`
  position: absolute;
  top: 6px;
  bottom: 0;
  width: ${(p) => p.$sideW || "10%"};
  right: ${(p) => `calc(-0.45 * ${p.$sideW || "10%"})`};
  background: ${(p) => p.$bg};
  border-bottom-right-radius: 6px;
  transform: skewY(-6deg);
  filter: drop-shadow(0 10px 16px rgba(0, 0, 0, 0.22));
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 10px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.6),
      rgba(255, 255, 255, 0)
    );
    opacity: 0.7;
  }
`;
const TopCap = styled.div`
  display: none;
`;

function TiltBar({
  h = 240,
  variant = "royal",
  tilt = 15,
  delay = 0,
  sidePct = 0.1,
  children,
}) {
  const prefs = useReducedMotion();
  const v = PALETTE[variant] || PALETTE.royal;
  const sideW = `${Math.round(sidePct * 1000) / 10}%`;
  return (
    <TiltWrap
      $h={h}
      $tilt={prefs ? 0 : tilt}
      initial={prefs ? false : { scaleY: 0, opacity: 0, filter: "blur(8px)" }}
      animate={{ scaleY: 1, opacity: 1, filter: "blur(0px)" }}
      transition={{
        type: "spring",
        stiffness: 110,
        damping: 18,
        duration: 1.15,
        delay,
      }}
      style={{ willChange: "transform" }}
    >
      <FrontFace $bg={v.front} />
      <RightFace $bg={v.side} $sideW={sideW} />
      <TopCap />
      {children}
    </TiltWrap>
  );
}

/* ================= Podium ================= */
const Card = styled.div`
  position: relative;
  border-radius: 20px;
  padding: 16px;
  height: 380px;
  overflow: hidden;
`;
const PodiumWrap = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: auto 1fr auto;
  row-gap: 8px;
`;
const PodiumStage = styled(motion.div)`
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 10px;
  padding: 12px 0 0;
`;
const Col = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 96px;
  margin: 0 -3px;
`;

/* 왕관 + 하이라이트(블루 분위기 맞춤) */
const crownSweep = keyframes`
  0%{ transform: translateX(-120%) rotate(16deg); opacity: 0; }
  15%{ opacity: .7; }
  50%{ opacity: .9; }
  100%{ transform: translateX(120%) rotate(16deg); opacity: 0; }
`;
const CrownWrap = styled(motion.div)`
  position: absolute;
  top: -98px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  &::after {
    content: "";
    position: absolute;
    top: -6px;
    left: -140%;
    width: 90px;
    height: 50px;
    pointer-events: none;
    background: linear-gradient(
      105deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.85) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    animation: ${crownSweep} 2.6s ease-in-out infinite;
  }
`;
const Crown = () => (
  <svg width="42" height="26" viewBox="0 0 64 38">
    <defs>
      <linearGradient id="royal-gold" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#FFF4A6" />
        <stop offset="45%" stopColor="#FFD966" />
        <stop offset="100%" stopColor="#E6B321" />
      </linearGradient>
    </defs>
    <path
      d="M4 32L12 10l12 10 8-18 8 18 12-10 8 22H4Z"
      fill="url(#royal-gold)"
      stroke="#B88900"
      strokeWidth="2"
    />
  </svg>
);

/* 텍스트 오버레이 */
const RankInBar = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 90%;
  text-align: center;
  font-weight: 900;
  letter-spacing: -0.4px;
  user-select: none;
  pointer-events: none;

  --rank: #12213f;
  --score: #1b2a4b;
  --name: #2a3a5a;
  --region: #0f172a;

  .rank {
    color: var(--rank);
    font-size: ${(p) =>
    p.$size === "lg"
      ? "clamp(36px,5.6vw,40px)"
      : p.$size === "md"
        ? "clamp(26px,5.0vw,32px)"
        : "clamp(22px,4.6vw,28px)"};
    line-height: 1.05;
    text-shadow: none;
  }
  .score {
    color: var(--score);
    white-space: nowrap;
    line-height: 1.05;
    font-size: clamp(13px, 3.2vw, 18px);
    letter-spacing: -0.2px;
  }
  .corpName {
    color: var(--name);

    font-weight: 900;
    font-size: clamp(16px, 2.8vw, 17px);
    line-height: 1.05;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .region {
    color: var(--region);
    margin-top: 30px;
    font-weight: 900;
    font-size: clamp(16px, 2.8vw, 17px);
    line-height: 1.05;
  }
`;

const CorpName = styled.div`
  margin-top: 18px;
  color: #e3edff;
  font-weight: 800;
  font-size: 15px;
  text-align: center;
  opacity: 0.95;
  white-space: nowrap;
  max-width: 112px;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StageBase = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 28px;
  width: 92%;
  height: 18px;
  border-radius: 999px;
  background: radial-gradient(
    60% 100% at 50% 50%,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0) 70%
  );
`;

/* 리스트 행 */
const Row = styled.div`
  display: grid;
  grid-template-columns: 56px max-content 1fr;
  align-items: center;
  gap: 10px 10px;
  padding: 14px;
  border-radius: 14px;
  background: ${(p) =>
    p.$late
      ? "linear-gradient(180deg,#D6E6FF 0%, #B6CEF9 100%)"
      : "linear-gradient(180deg,#C6DBFF 0%, #A8C6FF 100%)"};
  box-shadow: 0 6px 12px rgba(0,0,0,.06);
  color: #0d1b3f;
`;

const NameArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  .name{
    font-weight: 900; color: #0d1b3f; font-size: 15px; line-height: 1.1;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .region{
    font-weight: 900; color: #0d1b3f; font-size: 15px; line-height: 1.1;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    opacity: .95;
  }
  .amount{
    margin-top: 2px;
    font-weight: 900;
    font-size: 15px;
    color: #0d1b3f;
    width: 100%;
    text-align: right;
    white-space: nowrap;
  }
`;

const AvatarSmImg = styled.img`
  display: block;
  width: 30px;
  height: 60px;
  border-radius: 999px;
  object-fit: cover;
  background: transparent;
  margin-left: -6px;
`;

const RankBadgeSm = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: #fff;
  display: grid;
  place-items: center;
  font-weight: 900;
  color: #1a49d8;
  border: 2px solid rgba(26, 73, 216, 0.25);
`;

const Score = styled.div`
  font-weight: 900;
  color: #0d1b3f;
  white-space: nowrap;
  font-size: 16px;
  line-height: 1;
`;

/* 빛 번짐 */
const glowPulse = keyframes`
  0%,100%{ box-shadow: 0 6px 14px rgba(0,0,0,.22), 0 0 0px rgba(130,170,255,0); }
  50%{    box-shadow: 0 6px 14px rgba(0,0,0,.22), 0 0 24px rgba(130,170,255,.55); }
`;
const FirstGlow = styled.div`
  animation: ${glowPulse} 3s ease-in-out infinite;
`;

/* 리스트 (4~10위) */
const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* ===== Sparkle (twinkle) ===== */
const sparkleTwinkle = keyframes`
  0%   { transform: scale(0.6) rotate(0deg);   opacity: 0; }
  45%  { opacity: 1; }
  100% { transform: scale(1.08) rotate(180deg); opacity: 0; }
`;

const SPARKLE_THEME = {
  ice: { core: "#DFF1FF", glow: "160,205,255" },
  royal: { core: "#BFD6FF", glow: "135,170,255" },
  coral: { core: "#FFD3E0", glow: "255,170,195" },
  goldDeep: { core: "#FFC54D", glow: "255,200,90" },
};

const SparkleWrap = styled.div`
  position: absolute;
  left: 50%;
  width: 240px;
  height: 190px;
  transform: translateX(-50%);
  pointer-events: none;
  overflow: visible;
  z-index: 4;
`;

const Sparkle = styled.span`
  position: absolute;
  display: block;
  width: ${(p) => p.$s || 16}px;
  height: ${(p) => p.$s || 16}px;
  border-radius: 3px;
  --core: ${(p) => SPARKLE_THEME[p.$theme || "ice"]?.core};
  --glow: ${(p) => SPARKLE_THEME[p.$theme || "ice"]?.glow};
  background: radial-gradient(
    circle,
    #fff 0%,
    var(--core) 55%,
    rgba(0, 0, 0, 0) 72%
  );
  box-shadow:
    0 0 10px rgba(var(--glow), 0.95),
    0 0 22px rgba(var(--glow), 0.55),
    0 0 40px rgba(var(--glow), 0.35);
  filter: saturate(1.05);
  animation: ${sparkleTwinkle} ${(p) => p.$dur || 2.0}s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || 0}s;
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 170%;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.95),
      rgba(255, 255, 255, 0)
    );
    box-shadow: 0 0 10px rgba(var(--glow), 0.65);
  }
  &::after {
    transform: translate(-50%, -50%) rotate(90deg);
  }
`;

/* ================= Podium Component ================= */

const maskCorpName = (name) => {
  if (!name) return "";
  const clean = String(name).trim();
  if (clean.length <= 2) return "00";
  return `00${clean.slice(2)}`;
};

function Top3Podium({ items }) {
  const prefs = useReducedMotion();
  const sorted = useMemo(
    () => [...items].slice(0, 3).sort((a, b) => (b.sales || 0) - (a.sales || 0)),
    [items]
  );
  const first = sorted[0],
    second = sorted[1],
    third = sorted[2];
  const H1 = 256,
    H2 = 192,
    H3 = 160;

  // 이름/지역 + 마스킹
  const firstCorp = parseCorp(first?.corp);
  const secondCorp = parseCorp(second?.corp);
  const thirdCorp = parseCorp(third?.corp);

  const firstNameMasked = maskCorpName(firstCorp.name || "-");
  const secondNameMasked = maskCorpName(secondCorp.name || "-");
  const thirdNameMasked = maskCorpName(thirdCorp.name || "-");

  return (
    <PodiumWrap>
      <div />
      <PodiumStage
        initial="initial"
        animate="animate"
        transition={
          prefs
            ? undefined
            : { when: "beforeChildren", staggerChildren: 0.28, delayChildren: 0.1 }
        }
      >
        {/* 2위 */}
        <Col
          initial={{ y: 20, opacity: 0, scale: 0.96, filter: "blur(6px)" }}
          animate={{ y: 0, opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ type: "spring", stiffness: 120, damping: 18, duration: 0.9 }}
        >
          <CorpAvatarImg src={corp2} alt="2위 로고" $size={180} $topMul={-0.7} />
          <TiltBar h={H2} variant="steel" tilt={-15} delay={prefs ? 0 : 0.25} sidePct={0.1}>
            <RankInBar $size="md">
              <div className="rank">2위</div>
              {/* <div className="score">{formatKRW(second?.sales)}</div> */}
              <div className="region">{secondCorp.region}</div>
              <div className="corpName">{'운수사'}</div>

            </RankInBar>
          </TiltBar>
        </Col>

        {/* 1위 */}
        <Col
          style={{ zIndex: 2, transformOrigin: "bottom center" }}
          initial={{ y: 20, opacity: 0, scale: 0.96, filter: "blur(6px)" }}
          animate={{ y: 0, opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ type: "spring", stiffness: 120, damping: 18, duration: 1.0 }}
        >
          <CorpAvatarImg src={corp1} alt="1위 로고" $size={180} $topMul={-0.72} />

          <SparkleWrap>
            <Sparkle style={{ left: 18, top: 10 }} $s={18} $dur={1.0} $delay={0.0} $theme="royal" />
            <Sparkle style={{ left: 58, top: -12 }} $s={22} $dur={1.6} $delay={0.25} $theme="royal" />
            <Sparkle style={{ left: 98, top: -26 }} $s={14} $dur={1.7} $delay={0.5} $theme="royal" />
            <Sparkle style={{ left: 144, top: -6 }} $s={18} $dur={1.2} $delay={0.35} $theme="royal" />
            <Sparkle style={{ left: 178, top: 22 }} $s={15} $dur={1.0} $delay={0.9} $theme="royal" />
            <Sparkle style={{ left: 120, top: 40 }} $s={22} $dur={1.8} $delay={0.3} $theme="royal" />
            <Sparkle style={{ left: 72, top: 34 }} $s={17} $dur={1.1} $delay={0.7} $theme="royal" />
            <Sparkle style={{ left: 36, top: 28 }} $s={12} $dur={1.9} $delay={0.4} $theme="royal" />
            <Sparkle style={{ left: 156, top: 32 }} $s={12} $dur={2.3} $delay={0.6} $theme="royal" />
          </SparkleWrap>

          <TiltBar h={H1} variant="royal" tilt={0} delay={prefs ? 0 : 0.1} sidePct={0.1}>
            <RankInBar $size="lg">
              <div className="rank">1위</div>
              {/* <div className="score">{formatKRW(first?.sales)}</div> */}
              <div className="region">{firstCorp.region}</div>
              <div className="corpName">{'운수사'}</div>
           
            </RankInBar>
          </TiltBar>
        </Col>

        {/* 3위 */}
        <Col
          initial={{ y: 20, opacity: 0, scale: 0.96, filter: "blur(6px)" }}
          animate={{ y: 0, opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ type: "spring", stiffness: 120, damping: 18, duration: 0.9 }}
        >
          <CorpAvatarImg src={corp3} alt="3위 로고" $size={180} $topMul={-0.8} />
          <TiltBar h={H3} variant="coral" tilt={15} delay={prefs ? 0 : 0.4} sidePct={0.1}>
            <RankInBar $size="sm">
              <div className="rank">3위</div>
              {/* <div className="score">{formatKRW(third?.sales)}</div> */}
              <div className="region">{thirdCorp.region}</div>
              <div className="corpName">{'운수사'}</div>
      
            </RankInBar>
          </TiltBar>
        </Col>
      </PodiumStage>

      <StageBase />
    </PodiumWrap>
  );
}


/* ================= Page ================= */
export default function SalesRankingCorpPage() {
  const [data, setData] = useState([]);
  const [latestMonthLabel, setLatestMonthLabel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);

        // ✅ 법인 전용 랭킹 데이터
        const top = await fetchCorporateTopByFare(10);

        if (!alive) return;

        // ✅ 가장 최근 정산월 → "10월" 라벨
        if (top && top.latestMonthKey) {
          setLatestMonthLabel(formatSettlementMonthLabel(top.latestMonthKey));
        } else {
          setLatestMonthLabel("");
        }

        const mapped = (top || []).map((r, i) => ({
          rank: i + 1,
          corp: r.corp || r.franchiseName || r.user || "",
          sales: Number(r.sales ?? 0),
        }));

        setData(mapped);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const top3 = useMemo(() => (data || []).slice(0, 3), [data]);
  const rest = useMemo(() => (data || []).slice(3, 10), [data]);

  const subtitle = latestMonthLabel
    ? `법인 ${latestMonthLabel} 랭킹`
    : "법인 랭킹";

  return (
    <Wrap>
      <Sheet>
        <PageTopBar variant="back" label="뒤로" borderColor="#dde7fb" />

        <Body>
          <BrandHeader subtitle={subtitle} />

          <Card>
            {loading ? (
              <div
                style={{
                  color: "#12213F",
                  padding: 16,
                  fontWeight: 800,
                }}
              >
                랭킹을 불러오는 중…
              </div>
            ) : top3.length < 3 ? (
              <div
                style={{
                  color: "#12213F",
                  padding: 16,
                  fontWeight: 800,
                }}
              >
                데이터가 부족합니다.
              </div>
            ) : (
              <MotionConfig reducedMotion="user">
                <Top3Podium items={top3} />
              </MotionConfig>
            )}
          </Card>

          {/* 4~10위 리스트 */}
          {!loading && rest.length > 0 && (
            <ListWrap>
              {rest.map((item) => {
                const isLate = item.rank >= 6;
                const { name, region } = parseCorp(item.corp);
                const maskedName = maskCorpName(name);

                return (
                  <Row key={item.rank} $late={isLate}>
                    <RankBadgeSm>{item.rank}위</RankBadgeSm>
                    <AvatarSmImg src={corp4} alt={`${item.rank}위 로고`} />
                    <NameArea>
                      <div className="region">{region}</div>
                      <div className="name">{'운수사'}</div>
                   
                      {/* <div className="amount">{formatKRW(item.sales)}</div> */}
                    </NameArea>
                  </Row>
                );
              })}
            </ListWrap>
          )}
        </Body>
      </Sheet>
    </Wrap>
  );
}
